import React, { useEffect, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useConfig } from "../../../contexts/config";
import { useTheme } from "../../../contexts/ThemeContext";
import { getActivityLog } from "../../../services/moodle/activity";

const PAGE_SIZE = 15;

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

const formatRelativo = (timestamp) => {
  const diffSeg = Math.floor(Date.now() / 1000) - timestamp;
  if (diffSeg < 60) return "recién";
  if (diffSeg < 3600) return `hace ${Math.floor(diffSeg / 60)} min`;
  if (diffSeg < 86400) return `hace ${Math.floor(diffSeg / 3600)} h`;
  const dias = Math.floor(diffSeg / 86400);
  if (dias < 7) return dias === 1 ? "hace 1 día" : `hace ${dias} días`;
  const d = new Date(timestamp * 1000);
  return `${d.getDate()} ${MESES[d.getMonth()]}`;
};

export const Actividad = () => {
  const auth = useAuth();
  const { config } = useConfig();
  const { colors, mode } = useTheme() || { colors: {}, mode: "light" };
  const isDark = mode === "dark";

  const textPrimary = isDark ? "#f1f5f9" : "#1e293b";
  const textSecondary = isDark ? "#8b8a99" : "#64748b";
  const borderColor = isDark ? "rgba(255,255,255,0.05)" : "#e2e8f0";
  const accent = "#ECB236";
  const dotColor = isDark ? "rgba(255,255,255,0.28)" : "#cbd5e1";

  const [entries, setEntries] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const token = auth.user?.token;

  useEffect(() => {
    if (!token) {
      setCargando(false);
      return;
    }
    let cancelado = false;
    setCargando(true);
    getActivityLog({ token, baseUrl: config.baseURL, limit: PAGE_SIZE, offset: 0 })
      .then((rows) => {
        if (cancelado) return;
        setEntries(rows);
        setOffset(rows.length);
        setHasMore(rows.length === PAGE_SIZE);
        setError(null);
      })
      .catch(() => {
        if (!cancelado) setError("No se pudo cargar tu actividad reciente.");
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, config.baseURL]);

  const cargarMas = async () => {
    if (!token || cargando) return;
    setCargando(true);
    try {
      const rows = await getActivityLog({
        token,
        baseUrl: config.baseURL,
        limit: PAGE_SIZE,
        offset,
      });
      setEntries((prev) => [...prev, ...rows]);
      setOffset((prev) => prev + rows.length);
      setHasMore(rows.length === PAGE_SIZE);
    } catch {
      setError("No se pudo cargar más actividad.");
    } finally {
      setCargando(false);
    }
  };

  return (
    <div>
      {error && (
        <p style={{ fontSize: 13, color: "#e11d48", margin: 0 }}>{error}</p>
      )}

      {!cargando && !error && entries.length === 0 && (
        <p style={{ fontSize: 13, color: textSecondary, fontStyle: "italic", margin: 0 }}>
          Todavía no tienes actividad reciente.
        </p>
      )}

      <div style={{ position: "relative" }}>
          {entries.length > 0 && (
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                top: 8,
                bottom: 8,
                left: 3,
                width: 1,
                backgroundColor: borderColor,
              }}
            />
          )}

          {entries.map((entry) => {
            const destacado = entry.points > 0;
            return (
              <div
                key={entry.id}
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "baseline",
                  gap: 20,
                  paddingLeft: 24,
                  paddingBottom: 28,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: destacado ? -2 : 1,
                    top: 3,
                    width: destacado ? 11 : 7,
                    height: destacado ? 11 : 7,
                    borderRadius: "50%",
                    backgroundColor: destacado ? "#f5f3ed" : dotColor,
                  }}
                />
                <span
                  style={{
                    flex: "0 0 76px",
                    fontSize: 12,
                    color: textSecondary,
                  }}
                >
                  {formatRelativo(entry.timecreated)}
                </span>
                <p
                  style={{
                    margin: 0,
                    fontSize: 14,
                    lineHeight: 1.4,
                    color: destacado ? textPrimary : textSecondary,
                    fontWeight: destacado ? 600 : 400,
                  }}
                >
                  {entry.description}
                </p>
              </div>
            );
          })}
        </div>

        {cargando && entries.length === 0 && (
          <p style={{ fontSize: 13, color: textSecondary, margin: 0 }}>Cargando...</p>
        )}

        {hasMore && entries.length > 0 && (
          <button
            type="button"
            onClick={cargarMas}
            disabled={cargando}
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: accent,
              background: "none",
              border: "none",
              padding: 0,
              cursor: cargando ? "default" : "pointer",
              opacity: cargando ? 0.7 : 1,
            }}
          >
            {cargando ? "Cargando..." : "Ver más actividad"}
          </button>
        )}
    </div>
  );
};

export default Actividad;
